// frontend/src/hooks/useBearState.ts
// TaisaCore Sprint 10: Bear State Management Hook

import { useState, useEffect, useCallback, useContext } from 'react';
import { BearState, BearAlert } from '../types/dashboard';
import { AlertContext } from '../contexts/AlertContext';
import { AIProcessingContext } from '../contexts/AIProcessingContext';

interface UseBearStateReturn {
  bearState: BearState;
  currentAlert: BearAlert | null;
  dismissCurrentAlert: () => void;
  setBearState: (state: BearState) => void;
}

export const useBearState = (): UseBearStateReturn => {
  const [manualState, setManualState] = useState<BearState | null>(null);
  const alertContext = useContext(AlertContext);
  const aiContext = useContext(AIProcessingContext);

  // Get the current highest priority alert
  const currentAlert = alertContext?.criticalAlerts[0] || alertContext?.alerts[0] || null;

  // Dismiss the current alert
  const dismissCurrentAlert = useCallback(() => {
    if (currentAlert && alertContext) {
      alertContext.dismissAlert(currentAlert.id);
    }
  }, [currentAlert, alertContext]);

  // Determine bear state based on context
  const determineBearState = useCallback((): BearState => {
    // Manual override takes priority
    if (manualState) {
      return manualState;
    }

    // Check for critical alerts
    if (alertContext?.criticalAlerts && alertContext.criticalAlerts.length > 0) {
      return 'alert';
    }

    // Check for AI processing
    if (aiContext?.isProcessing) {
      return 'thinking';
    }

    // Check for recent success
    if (aiContext?.lastSuccess) {
      const timeSinceSuccess = Date.now() - aiContext.lastSuccess;
      if (timeSinceSuccess < 3000) { // Show success for 3 seconds
        return 'success';
      }
    }

    // Check for any alerts
    if (alertContext?.alerts && alertContext.alerts.length > 0) {
      return 'watching';
    }

    // Default idle state
    return 'idle';
  }, [manualState, alertContext, aiContext]);

  const bearState = determineBearState();

  // Auto-clear manual state after a timeout
  useEffect(() => {
    if (manualState) {
      const timeout = setTimeout(() => {
        setManualState(null);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [manualState]);

  // Clear success state after animation
  useEffect(() => {
    if (bearState === 'success') {
      const timeout = setTimeout(() => {
        // Force re-render to check state again
        setManualState(null);
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [bearState]);

  return {
    bearState,
    currentAlert,
    dismissCurrentAlert,
    setBearState: setManualState,
  };
};

export default useBearState;
